import React, { useState } from 'react';
import { RefreshCw, X, Download, Clock, ChevronDown, ChevronUp, Star, Bug, Plus } from 'lucide-react';
import { getLatestVersionChanges } from '../changelog';

const VersionUpdateNotification = ({ 
  showUpdatePrompt, 
  versionInfo, 
  updateToNewVersion, 
  ignoreUpdate, 
  setShowUpdatePrompt 
}) => {
  const [showChangelog, setShowChangelog] = useState(false);
  const latestChanges = getLatestVersionChanges();

  if (!showUpdatePrompt) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Download className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Nueva Versión Disponible
            </h3>
          </div>
          <button
            onClick={() => setShowUpdatePrompt(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-gray-600">
            Se ha detectado una nueva versión de la aplicación. Para obtener las últimas 
            funcionalidades y correcciones, te recomendamos actualizar ahora.
          </p>

          {/* Version Info */}
          {versionInfo && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Versión actual:</span>
                <span className="text-sm font-mono text-gray-800">
                  {versionInfo.current}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Nueva versión:</span>
                <span className="text-sm font-mono text-green-600">
                  {versionInfo.server}
                </span>
              </div>
            </div>
          )}

          {/* Changelog Toggle */}
          <div className="bg-blue-50 rounded-lg p-3">
            <button
              onClick={() => setShowChangelog(!showChangelog)}
              className="flex items-center justify-between w-full text-left"
            >
              <h4 className="text-sm font-medium text-blue-900">
                ¿Qué incluye esta actualización?
              </h4>
              {showChangelog ? (
                <ChevronUp className="h-4 w-4 text-blue-600" />
              ) : (
                <ChevronDown className="h-4 w-4 text-blue-600" />
              )}
            </button>
            
            {showChangelog && latestChanges && (
              <div className="mt-3 space-y-3">
                {/* Version Info */}
                <div className="bg-white rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-800">
                      {latestChanges.version} - {latestChanges.date}
                    </span>
                  </div>
                  
                  {/* Highlights */}
                  {latestChanges.highlights && latestChanges.highlights.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-xs font-medium text-gray-700 mb-1">Destacados:</h5>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {latestChanges.highlights.map((highlight, index) => (
                          <li key={index} className="flex items-start gap-1">
                            <span className="text-green-600">•</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Changes by Category */}
                {latestChanges.changes && (
                  <div className="space-y-2">
                    {/* Nuevas Funcionalidades */}
                    {latestChanges.changes.new && latestChanges.changes.new.length > 0 && (
                      <div className="bg-green-50 rounded-lg p-2 border border-green-200">
                        <div className="flex items-center gap-1 mb-1">
                          <Plus className="h-3 w-3 text-green-600" />
                          <span className="text-xs font-medium text-green-800">Nuevas Funcionalidades</span>
                        </div>
                        <ul className="text-xs text-green-700 space-y-0.5">
                          {latestChanges.changes.new.slice(0, 3).map((change, index) => (
                            <li key={index} className="flex items-start gap-1">
                              <span className="text-green-600">•</span>
                              <span>{change}</span>
                            </li>
                          ))}
                          {latestChanges.changes.new.length > 3 && (
                            <li className="text-green-600 text-xs">
                              +{latestChanges.changes.new.length - 3} más...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Mejoras */}
                    {latestChanges.changes.improved && latestChanges.changes.improved.length > 0 && (
                      <div className="bg-blue-50 rounded-lg p-2 border border-blue-200">
                        <div className="flex items-center gap-1 mb-1">
                          <Star className="h-3 w-3 text-blue-600" />
                          <span className="text-xs font-medium text-blue-800">Mejoras</span>
                        </div>
                        <ul className="text-xs text-blue-700 space-y-0.5">
                          {latestChanges.changes.improved.slice(0, 2).map((change, index) => (
                            <li key={index} className="flex items-start gap-1">
                              <span className="text-blue-600">•</span>
                              <span>{change}</span>
                            </li>
                          ))}
                          {latestChanges.changes.improved.length > 2 && (
                            <li className="text-blue-600 text-xs">
                              +{latestChanges.changes.improved.length - 2} más...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Correcciones */}
                    {latestChanges.changes.fixed && latestChanges.changes.fixed.length > 0 && (
                      <div className="bg-orange-50 rounded-lg p-2 border border-orange-200">
                        <div className="flex items-center gap-1 mb-1">
                          <Bug className="h-3 w-3 text-orange-600" />
                          <span className="text-xs font-medium text-orange-800">Correcciones</span>
                        </div>
                        <ul className="text-xs text-orange-700 space-y-0.5">
                          {latestChanges.changes.fixed.slice(0, 2).map((change, index) => (
                            <li key={index} className="flex items-start gap-1">
                              <span className="text-orange-600">•</span>
                              <span>{change}</span>
                            </li>
                          ))}
                          {latestChanges.changes.fixed.length > 2 && (
                            <li className="text-orange-600 text-xs">
                              +{latestChanges.changes.fixed.length - 2} más...
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 mt-6">
          <button
            onClick={updateToNewVersion}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Actualizar Ahora</span>
          </button>
          
          <button
            onClick={ignoreUpdate}
            className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center space-x-2"
          >
            <Clock className="h-4 w-4" />
            <span>Después</span>
          </button>
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-500 text-center mt-4">
          La actualización limpiará el cache del navegador y recargará la aplicación
        </p>
      </div>
    </div>
  );
};

export default VersionUpdateNotification;
